import {
  FormControl,
  FormControlProps,
  FormHelperTextProps,
  FormLabelProps,
  Radio,
  RadioGroup,
  RadioGroupProps,
} from "@mui/material";
import { getAriaDescribedByValue } from "core/model/utils/strings";
import { Chip, ChipProps } from "ds_legacy/components/Chip";
import { dp, margin, sizing } from "ds_legacy/materials/metrics";
import { getFocusVisibleOutline } from "ds_legacy/materials/theme";
import { FONT_SIZE_16 } from "ds_legacy/materials/typography";
import { uniqueId } from "lodash";
import { CSSProperties, ReactNode, useState } from "react";
import {
  FieldValidation,
  FormElement,
  FormElementProps,
  isValid,
} from "react-forms-state";
import { ChipOption, getOrderedItems } from "../ChipGroup";
import { SubheadingFormLegend } from "../FormComponents/SubheadingFormLegend";
import { Validation } from "../Validation";

export type ConnectedRadioChipGroupProps = RadioChipGroupProps &
  FormElementProps;

export type RadioChipGroupProps = {
  ariaDescribedBy?: string;
  chipStyle?: {
    height?: CSSProperties["height"];
    justifyContent?: CSSProperties["justifyContent"];
    width?: CSSProperties["width"];
  };
  className?: string;
  elementName: string;
  formControlSx?: FormControlProps["sx"];
  formHelperTextSx?: FormHelperTextProps["sx"];
  formLabelSx?: FormLabelProps["sx"];
  id: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  isSquared?: boolean;
  label: string;
  LabelInfo?: ReactNode;
  labelWrapperStyle?: CSSProperties;
  onChange: (value: number) => void;
  options: Array<RadioChipOption>;
  radioGroupSx?: RadioGroupProps["sx"];
  sortOrder?: Record<string, any>;
  validation?: FieldValidation;
  value: Array<number> | number | undefined;
};

export type RadioChipOption = {
  disabled?: boolean;
  name: string;
  value: any;
};

function getId(elementName: string, name: string) {
  return `chip-${elementName}-${name
    .replace(/[ :]/g, "")
    .replace("<", "below")}`;
}

const HoverRadio = ({
  ariaDescribedBy,
  chipProps,
  elementName,
  errorTextId,
  id,
  onChange,
  option,
  required,
  validation,
}: {
  ariaDescribedBy?: string;
  chipProps: ChipProps;
  elementName: RadioChipGroupProps["elementName"];
  errorTextId: string;
  id: string;
  onChange: RadioChipGroupProps["onChange"];
  option: ChipOption;
  required: RadioChipGroupProps["isRequired"];
  validation: RadioChipGroupProps["validation"];
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Radio
      checked={chipProps.isSelected}
      checkedIcon={
        <Chip {...chipProps} isHovered={hovered}>
          {option.name}
        </Chip>
      }
      disabled={chipProps.isDisabled}
      disableTouchRipple
      focusRipple
      icon={
        <Chip {...chipProps} isHovered={hovered}>
          {option.name}
        </Chip>
      }
      id={id}
      inputProps={{
        ...{ ["data-testid"]: id },
        "aria-describedby": getAriaDescribedByValue([
          !isValid(validation) && errorTextId,
          ariaDescribedBy,
        ]),
      }}
      name={elementName}
      required={required}
      sx={{
        ":hover": { backgroundColor: "transparent" },
        margin: "0",
        // required for accessiblity to ensure focus outline is visible
        outlineOffset: dp(1),
        padding: "0",
        ...getFocusVisibleOutline(),
        borderRadius: 1,
        width: chipProps?.width,
      }}
      value={option.value}
      onChange={() => {
        onChange(option.value);
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    />
  );
};

export function RadioChipGroup({
  ariaDescribedBy,
  chipStyle,
  elementName,
  formControlSx,
  formHelperTextSx,
  formLabelSx,
  id,
  isDisabled,
  isRequired,
  isSquared,
  label,
  LabelInfo,
  labelWrapperStyle,
  onChange,
  options,
  radioGroupSx,
  sortOrder,
  validation,
  value,
}: RadioChipGroupProps) {
  const sortedOptions = getOrderedItems(sortOrder, options);
  const errorTextId = `${elementName}-error-text`;
  const hasError = isValid(validation) === false;
  const uniqueLabelId = uniqueId(`${id}-label`);

  if (!label?.length || !id?.length) {
    console.error(
      "[RadioChipGroup] Error: Component lacks 'id' or 'label' for accessibility.",
    );
  }

  return (
    <FormControl
      component="fieldset"
      error={!isValid(validation)}
      sx={{ ...formControlSx }}
    >
      <div style={labelWrapperStyle}>
        <SubheadingFormLegend
          error={hasError}
          id={uniqueLabelId}
          required={isRequired}
          sx={{
            fontSize: FONT_SIZE_16,
            ...formLabelSx,
          }}
        >
          {label}
        </SubheadingFormLegend>
        {LabelInfo}
      </div>
      <RadioGroup
        aria-labelledby={`${uniqueLabelId} ${
          !isValid(validation) ? errorTextId : ""
        } ${ariaDescribedBy ?? ""}`.trim()}
        id={id}
        row
        sx={{
          gap: isSquared ? sizing(1) : sizing(1.5),
          margin: margin(1, 0, 0, 2),
          ...radioGroupSx,
        }}
        value={value}
      >
        {sortedOptions.map((option) => {
          const optionIsDisabled = option.disabled || isDisabled;
          const isSelected = option.value === value;
          const optionId = getId(elementName, option.name);

          const chipProps: ChipProps = {
            className: !optionIsDisabled && isSelected ? "checked" : undefined,
            hasError: hasError,
            isBold: isSelected,
            isDisabled: optionIsDisabled,
            isPrimary: isSelected,
            isSelected: isSelected,
            isSquared: isSquared,
            opacity: optionIsDisabled && isSelected ? 0.5 : undefined,
            ...chipStyle,
          };

          return (
            <label key={`chip-${option.name}`} htmlFor={optionId}>
              <HoverRadio
                ariaDescribedBy={ariaDescribedBy}
                chipProps={chipProps}
                elementName={elementName}
                errorTextId={errorTextId}
                id={optionId}
                option={option}
                required={isRequired}
                validation={validation}
                onChange={onChange}
              />
            </label>
          );
        })}
      </RadioGroup>
      <Validation
        elementName={elementName}
        formHelperTextSx={formHelperTextSx}
        hasCustomValidation
        id={errorTextId}
        validation={validation}
      />
    </FormControl>
  );
}

const ConnectedRadioChipGroup =
  FormElement()<ConnectedRadioChipGroupProps>(RadioChipGroup);

export default ConnectedRadioChipGroup;
